import React from "react";
import { Link } from "react-router-dom";
import { footerLogo, footerMockupLight } from "assets/images";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
} from "react-icons/fa";
import { navbarConfig } from "../../reusables/Navbar/navConfig";

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-slate-50 text-gray-700 pt-10 pb-10 overflow-hidden font-sans border-t border-gray-200">
      {/* Background Gradient & Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[100px]" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-100/50 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-b border-gray-200 pb-12">
          {/* Left Column: Brand & Social */}
          <div className="md:col-span-3 space-y-6">
            <Link to="/" className="block">
              <img
                src={footerLogo}
                alt="CashQuin Logo"
                className="h-24 object-contain"
              />
            </Link>
            <p className="text-gray-500 text-base leading-relaxed max-w-sm font-medium">
              Experience the future of digital payments. Unified, secure, and
              simpler than ever before. Join the revolution today.
            </p>
            <div className="flex gap-4 pt-2">
              {[
                { icon: FaFacebookF, href: "#" },
                { icon: FaTwitter, href: "#" },
                { icon: FaInstagram, href: "#" },
                { icon: FaLinkedinIn, href: "#" },
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-500 hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 hover:text-white transition-all duration-300 shadow-sm hover:shadow-lg ring-1 ring-gray-200 hover:ring-0"
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Middle Columns: Quick Links */}
          <div className="md:col-span-2 space-y-6">
            <h4 className="text-lg font-bold text-gray-900">Company</h4>
            <ul className="space-y-3 nav-links text-gray-500 text-sm font-medium">
              <li>
                <Link
                  to="/aboutUs"
                  className="hover:text-blue-600 hover:translate-x-1 transition-all inline-block"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/careers"
                  className="hover:text-blue-600 hover:translate-x-1 transition-all inline-block"
                >
                  Careers
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="hover:text-blue-600 hover:translate-x-1 transition-all inline-block"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/press"
                  className="hover:text-blue-600 hover:translate-x-1 transition-all inline-block"
                >
                  Press
                </Link>
              </li>
            </ul>
          </div>

          {/* New Column: Our Services */}
          <div className="md:col-span-3 space-y-6">
            <h4 className="text-lg font-bold text-gray-900">Our Services</h4>
            <div className="space-y-6 text-gray-500 text-sm font-medium">
              {navbarConfig.map((category) => (
                <div key={category.id}>
                  <h5 className="font-semibold text-gray-800 mb-2">
                    {category.label}
                  </h5>
                  <ul className="space-y-2">
                    {category.children?.slice(0, 2).map((subItem) => (
                      <li key={subItem.id}>
                        <Link
                          to={subItem.path || "#"}
                          className="hover:text-blue-600 hover:translate-x-1 transition-all inline-block"
                        >
                          {subItem.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 space-y-6">
            <h4 className="text-lg font-bold text-gray-900">
              Contact & Support
            </h4>
            <ul className="space-y-4 text-gray-500 text-sm font-medium">
              <li className="flex items-start gap-3">
                <span className="mt-1 block w-2 h-2 rounded-full bg-blue-500 shrink-0 shadow-sm" />
                <span>
                  Webtooth Techno LLP <br /> Sector-49 Gurugram, Haryana
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0 shadow-sm" />
                <a
                  href="tel:+919511520346"
                  className="hover:text-blue-600 transition-colors"
                >
                  +91 9511520346
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 shadow-sm" />
                <a
                  href="mailto:connect@cashquin.com"
                  className="hover:text-blue-600 transition-colors"
                >
                  connect@cashquin.com
                </a>
              </li>
            </ul>
          </div>

          {/* Right Column: Visual CTA */}
          <div className="md:col-span-2 relative h-full flex items-center justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100 to-purple-100 rounded-full blur-2xl opacity-50 group-hover:opacity-75 transition duration-500"></div>
              <img
                src={footerMockupLight}
                alt="App Preview"
                className="relative transform transition-transform duration-500 hover:-translate-y-2 max-h-60 object-contain mx-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="text-gray-400 text-center text-sm pt-8 flex flex-col md:flex-row justify-between items-center gap-4 font-medium">
          <p>
            © 2025 Webtooth Techno LLP. All rights reserved. | A Digital India
            Initiative
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy-policy"
              className="hover:text-blue-600 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms-and-conditions"
              className="hover:text-blue-600 transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/refunds-cancellations"
              className="hover:text-blue-600 transition-colors"
            >
              Refunds
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
